import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { drawDashboard } from "./dashboard";
import { makeNodeCanvas, MODULES } from "./nodeTexture";

/**
 * PlatformViz — the full WebGL visualization for Content_1.
 *
 * A true-3D floating monitor (the unified platform) renders a live, animated
 * enterprise dashboard to a CanvasTexture. On scroll, the camera eases down to
 * reveal a neural-network ecosystem: module cards scattered at random below the
 * monitor, drifting gently, woven into a mesh of white "filler" nodes linked by
 * polygonal lines (cursor-responsive: parallax + repulsion + highlight). Light
 * pulses flow from each module up into the monitor. Single renderer, capped DPR,
 * throttled dashboard repaint, honors prefers-reduced-motion, full teardown.
 *
 * @param {string} [className]
 */
const PlatformViz = ({ className = "" }) => {
  const mountRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    const canvas = canvasRef.current;
    if (!mount || !canvas) return undefined;

    const section = mount.closest("section") || mount.parentElement;
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const smoothstep = (a, b, x) => {
      const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
      return t * t * (3 - 2 * t);
    };

    // -------------------------------------------------- renderer / scene
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 1.4, 14.5);

    scene.add(new THREE.AmbientLight(0xdfeaf5, 0.75));
    const key = new THREE.PointLight(0xffffff, 1.25, 80); // white key (no green tint)
    key.position.set(6, 9, 12);
    scene.add(key);
    const rim = new THREE.PointLight(0x3bb78f, 0.5, 70); // faint teal rim
    rim.position.set(-8, -2, 6);
    scene.add(rim);

    const disposables = [];
    const track = (obj) => {
      disposables.push(obj);
      return obj;
    };

    // round white dot sprite — shared by pulses, fillers, and ambient particles
    // so every point renders as a circle (not a square)
    const dotCanvas = document.createElement("canvas");
    dotCanvas.width = 64;
    dotCanvas.height = 64;
    const dctx = dotCanvas.getContext("2d");
    const dg = dctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    dg.addColorStop(0, "rgba(255,255,255,1)");
    dg.addColorStop(0.45, "rgba(255,255,255,0.9)");
    dg.addColorStop(1, "rgba(255,255,255,0)");
    dctx.fillStyle = dg;
    dctx.beginPath();
    dctx.arc(32, 32, 32, 0, Math.PI * 2);
    dctx.fill();
    const dotTex = track(new THREE.CanvasTexture(dotCanvas));

    // -------------------------------------------------- browser window (true 3D)
    // The company's focus is WEB apps, so the "platform" is shown as a sleek
    // browser window — a rounded glass slab with a title bar (window dots + URL
    // pill) framing the live dashboard. Sized +35% vs the old monitor.
    const BROWSER_W = 11.4; // wide 16:9 — reads like a maximized browser window
    const BROWSER_H = 6.4; // matches the chrome+dashboard texture aspect (1280x720)
    const DEPTH = 0.3;
    const monitor = new THREE.Group(); // (still named "monitor" everywhere downstream)
    monitor.position.set(0, 1.7, 0);
    scene.add(monitor);

    const roundedRectShape = (w, h, r) => {
      const s = new THREE.Shape();
      const x = -w / 2;
      const y = -h / 2;
      s.moveTo(x + r, y);
      s.lineTo(x + w - r, y);
      s.absarc(x + w - r, y + r, r, -Math.PI / 2, 0, false);
      s.lineTo(x + w, y + h - r);
      s.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2, false);
      s.lineTo(x + r, y + h);
      s.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI, false);
      s.lineTo(x, y + r);
      s.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
      return s;
    };

    // window body: solid rounded slab with slight depth + bevel (sleek edge)
    const bodyShape = roundedRectShape(BROWSER_W, BROWSER_H, 0.28);
    const bodyGeo = track(
      new THREE.ExtrudeGeometry(bodyShape, {
        depth: DEPTH,
        bevelEnabled: true,
        bevelThickness: 0.04,
        bevelSize: 0.06,
        bevelSegments: 2,
        steps: 1,
      })
    );
    bodyGeo.center();
    bodyGeo.computeBoundingBox();
    const bodyFrontZ = bodyGeo.boundingBox.max.z; // actual front face (incl. bevel)
    const bodyMat = track(
      new THREE.MeshStandardMaterial({ color: 0x0b1424, metalness: 0.55, roughness: 0.38 })
    );
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    monitor.add(body);

    // composite screen texture = browser chrome (title bar) + dashboard
    const dashCanvas = document.createElement("canvas");
    dashCanvas.width = 1280;
    dashCanvas.height = 720;
    const dashCtx = dashCanvas.getContext("2d");
    const dashTex = track(new THREE.CanvasTexture(dashCanvas));
    dashTex.colorSpace = THREE.SRGBColorSpace;
    dashTex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    dashTex.minFilter = THREE.LinearFilter; // no mipmaps → sharper screen text
    dashTex.magFilter = THREE.LinearFilter;
    dashTex.generateMipmaps = false;
    const screenGeo = track(new THREE.PlaneGeometry(BROWSER_W - 0.16, BROWSER_H - 0.16));
    // transparent:true so the rounded-corner texture lets the slab show at corners
    const screenMat = track(new THREE.MeshBasicMaterial({ map: dashTex, transparent: true }));
    const screen = new THREE.Mesh(screenGeo, screenMat);
    screen.position.z = bodyFrontZ + 0.03; // sit just in front of the slab (no occlusion)
    monitor.add(screen);

    const monitorAnchor = new THREE.Vector3(0, monitor.position.y - BROWSER_H / 2 - 0.2, 0.2);

    // -------------------------------------------------- curated neural network
    // Explicit graph (cards + plain junction dots + peripheral dots) joined by a
    // fixed edge list — see content/img/SAMPLE.png. Lines + dots are rebuilt each
    // frame from live node positions, so the subtle per-node drift never
    // disconnects them.
    const NODE_W = 2.25;
    const NODE_H = NODE_W * (206 / 376); // match card texture aspect (incl. glow margin)
    const nodeGeo = track(new THREE.PlaneGeometry(NODE_W, NODE_H));

    const rnd = (a, b) => a + Math.random() * (b - a);
    const makeGNode = (x, y, mesh) => ({
      bx: x, by: y, bz: rnd(-1.2, 1.2), // fixed base position
      x, y, z: 0, // live position (recomputed each frame)
      mesh: mesh || null,
      mi: -1, // module index (cards only)
      hover: 0,
      // subtle oscillation (small amplitude → cards/nodes drift but never re-order)
      ax: rnd(0.14, 0.28), ay: rnd(0.12, 0.24), az: rnd(0.1, 0.2),
      fx: rnd(0.12, 0.26), fy: rnd(0.12, 0.26), fz: rnd(0.1, 0.2),
      px: rnd(0, 6.28), py: rnd(0, 6.28), pz: rnd(0, 6.28),
    });

    const gnodes = [];
    const HUB = 0; // first node = hub (just below the browser; trunk attaches here)

    // plain junction dots (index 0 = HUB)
    const junctionLayout = [
      [0.0, -2.7], [-5.2, -4.8], [-2.0, -5.9], [3.0, -5.0], [5.0, -3.7],
      [-3.2, -6.9], [4.2, -7.0], [7.1, -6.6], [-6.6, -5.7],
    ];
    junctionLayout.forEach(([x, y]) => gnodes.push(makeGNode(x, y, null)));

    // cards — chosen placement resembling SAMPLE.png
    const cardLayout = [
      ["sales", -7.5, -3.6],
      ["finance", -3.5, -4.7],
      ["analytics", -8.6, -6.3],
      ["operations", 0.6, -3.4],
      ["inventory", 1.4, -6.7],
      ["procurement", 6.6, -5.4],
      ["hr", 8.6, -4.0],
    ];
    const cardIdx = {}; // kind -> gnode index
    const cardMeshes = [];
    cardLayout.forEach(([kind, x, y]) => {
      const mi = MODULES.findIndex((m) => m.kind === kind);
      const m = MODULES[mi];
      const cnv = makeNodeCanvas(m.label, m.kind, m.accent);
      const tex = track(new THREE.CanvasTexture(cnv));
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
      tex.minFilter = THREE.LinearFilter; // no mipmaps → crisp text
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = false;
      const mat = track(new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0 }));
      const mesh = new THREE.Mesh(nodeGeo, mat);
      mesh.renderOrder = 5; // draw cards on top of the network lines/dots
      scene.add(mesh);
      const gi = gnodes.length;
      const n = makeGNode(x, y, mesh);
      n.mi = mi;
      gnodes.push(n);
      cardIdx[kind] = gi;
      mesh.userData = { gi };
      cardMeshes.push(mesh);
    });

    // edge list (index pairs). C(kind)=card node index; plain numbers = junctions
    const C = (k) => cardIdx[k];
    const edges = [
      [0, C("operations")], [0, 4], [0, C("finance")],
      [C("operations"), C("sales")], [C("operations"), 3], [C("operations"), C("inventory")],
      [C("sales"), 1], [C("sales"), C("finance")],
      [C("finance"), 1], [C("finance"), 2],
      [1, C("analytics")], [1, 8], [C("analytics"), 8], [8, 5],
      [2, C("inventory")], [2, 5], [C("inventory"), 6],
      [3, C("inventory")], [3, C("procurement")],
      [4, C("hr")], [4, C("procurement")],
      [C("procurement"), 7], [C("procurement"), 6], [C("hr"), 7], [6, 7],
    ];

    // peripheral dots to fill empty space (left / right / bottom). Each links to
    // its TWO nearest existing nodes → triangulated fill (the red strokes), and
    // those new nodes sit at the intersections.
    const PERIPHERAL = 15;
    for (let i = 0; i < PERIPHERAL; i += 1) {
      const zone = i % 4; // 0 left, 1 right, 2 & 3 bottom (more density at bottom)
      let x; let y;
      if (zone === 0) { x = rnd(-13.5, -10.5); y = rnd(-4, -8); }
      else if (zone === 1) { x = rnd(10.5, 13.5); y = rnd(-4, -8); }
      // bottom: pull the center dots inward (x) and up (y) so the dangling
      // triangles read as a compact cluster instead of long, spread-out lines
      else { x = rnd(-6.5, 6.5); y = rnd(-7.6, -8.9); }
      const gi = gnodes.length;
      gnodes.push(makeGNode(x, y, null));
      // two nearest existing nodes → forms triangles
      let b1 = -1; let d1 = Infinity; let b2 = -1; let d2 = Infinity;
      for (let k = 0; k < gi; k += 1) {
        const dx = gnodes[k].bx - x; const dy = gnodes[k].by - y;
        const d = dx * dx + dy * dy;
        if (d < d1) { d2 = d1; b2 = b1; d1 = d; b1 = k; }
        else if (d < d2) { d2 = d; b2 = k; }
      }
      if (b1 >= 0) edges.push([gi, b1]);
      if (b2 >= 0) edges.push([gi, b2]);
    }

    const NET_PTS = gnodes.length;

    // adjacency (static) for pulse routing
    const adj = Array.from({ length: NET_PTS }, () => []);
    edges.forEach(([a, b]) => { adj[a].push(b); adj[b].push(a); });

    // node dots — all non-card nodes (hub + junctions + peripheral), slightly big
    const dotIdx = [];
    gnodes.forEach((n, k) => { if (!n.mesh) dotIdx.push(k); });
    const dotPos = new Float32Array(dotIdx.length * 3);
    const dotGeo = track(new THREE.BufferGeometry());
    dotGeo.setAttribute("position", new THREE.BufferAttribute(dotPos, 3));
    const dotMat = track(new THREE.PointsMaterial({
      map: dotTex, color: 0xffffff, size: 0.34, // ~20px node dots
      transparent: true, opacity: 0, depthWrite: false,
    }));
    scene.add(new THREE.Points(dotGeo, dotMat));

    // thick network lines (fat lines, px width)
    const LINE_PX = 3; // line thickness in px (knob — see notes)
    const lineFlat = new Float32Array(edges.length * 6);
    const lineGeo = track(new LineSegmentsGeometry());
    lineGeo.setPositions(lineFlat);
    const lineMat = track(new LineMaterial({
      color: 0xbcd0e2, linewidth: LINE_PX, transparent: true, opacity: 0, depthWrite: false,
    }));
    const netLines = new LineSegments2(lineGeo, lineMat);
    netLines.frustumCulled = false;
    scene.add(netLines);

    // single thick trunk: hub → computer
    const TRUNK_PX = 5;
    const trunkGeo = track(new LineSegmentsGeometry());
    trunkGeo.setPositions(new Float32Array(6));
    const trunkMat = track(new LineMaterial({
      color: 0x6fe0b4, linewidth: TRUNK_PX, transparent: true, opacity: 0, depthWrite: false,
    }));
    const trunkLine = new LineSegments2(trunkGeo, trunkMat);
    trunkLine.frustumCulled = false;
    scene.add(trunkLine);

    // ----------------------------- hopping pulses (mesh → hub → computer) -----
    // Each pulse walks edge-to-edge greedily toward the hub, then rides the trunk
    // up into the computer and respawns. Circular + slow (unchanged behavior).
    const PULSES = 35; //number of pulses representing data flowing through the network (knob — see notes)
    const pulses = [];
    for (let i = 0; i < PULSES; i += 1) {
      const from = 1 + Math.floor(Math.random() * (NET_PTS - 1)); // not the hub
      pulses.push({ from, to: from, t: 1, onTrunk: false, speed: (0.18 + Math.random() * 0.08) * 2.5 }); // 2.5 for pulse speed
    }
    const pulsePos = new Float32Array(PULSES * 3);
    const pulseGeo = track(new THREE.BufferGeometry());
    pulseGeo.setAttribute("position", new THREE.BufferAttribute(pulsePos, 3));
    const pulseMat = track(
      new THREE.PointsMaterial({
        map: dotTex,
        color: 0xffffff,
        size: 0.2,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    scene.add(new THREE.Points(pulseGeo, pulseMat));

    // -------------------------------------------------- ambient particles (neutral)
    const P_COUNT = 240;
    const pPos = new Float32Array(P_COUNT * 3);
    for (let i = 0; i < P_COUNT; i += 1) {
      pPos[i * 3] = (Math.random() - 0.5) * 36;
      pPos[i * 3 + 1] = (Math.random() - 0.5) * 26;
      pPos[i * 3 + 2] = (Math.random() - 0.5) * 16 - 3;
    }
    const partGeo = track(new THREE.BufferGeometry());
    partGeo.setAttribute("position", new THREE.BufferAttribute(pPos, 3));
    const partMat = track(
      new THREE.PointsMaterial({
        map: dotTex,
        color: 0xdfe8f2,
        size: 0.07,
        transparent: true,
        opacity: 0.34,
        depthWrite: false,
      })
    );
    const particles = new THREE.Points(partGeo, partMat);
    scene.add(particles);

    // -------------------------------------------------- sizing
    const setSize = () => {
      const w = mount.clientWidth || 1;
      const h = mount.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      lineMat.resolution.set(w, h); // fat lines need viewport px for correct width
      trunkMat.resolution.set(w, h);
    };
    setSize();
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(setSize);
      resizeObserver.observe(mount);
    } else {
      window.addEventListener("resize", setSize);
    }

    // -------------------------------------------------- scroll progress
    let progress = 0;
    let scrollScheduled = false;
    const computeProgress = () => {
      scrollScheduled = false;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
    };
    const onScroll = () => {
      if (scrollScheduled) return;
      scrollScheduled = true;
      requestAnimationFrame(computeProgress);
    };
    computeProgress();
    window.addEventListener("scroll", onScroll, { passive: true });

    // -------------------------------------------------- pointer
    const pointer = new THREE.Vector2(0, 0);
    const parallax = { x: 0, y: 0 };
    const raycaster = new THREE.Raycaster();
    let hovered = -1;
    const onPointerMove = (e) => {
      const rect = mount.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      pointer.set(nx, -ny);
      parallax.x = nx * 0.35;
      parallax.y = -ny * 0.2;
    };
    const onPointerLeave = () => {
      parallax.x = 0;
      parallax.y = 0;
      pointer.set(0, 0);
    };
    mount.addEventListener("pointermove", onPointerMove);
    mount.addEventListener("pointerleave", onPointerLeave);

    // -------------------------------------------------- browser texture repaint (throttled)
    // Draws the browser chrome (title bar) + the live dashboard below it, clipped
    // to a rounded window so the slab shows rounded corners.
    const CHROME_H = 86; // px of the texture used by the title bar
    const WIN_R = 26; // rounded-window corner radius (px)
    const ctxRoundRect = (c, x, y, w, h, r) => {
      const rr = Math.min(r, w / 2, h / 2);
      c.beginPath();
      c.moveTo(x + rr, y);
      c.arcTo(x + w, y, x + w, y + h, rr);
      c.arcTo(x + w, y + h, x, y + h, rr);
      c.arcTo(x, y + h, x, y, rr);
      c.arcTo(x, y, x + w, y, rr);
      c.closePath();
    };
    let lastDash = -1;
    const repaintDashboard = (time) => {
      const W = dashCanvas.width;
      const H = dashCanvas.height;
      dashCtx.clearRect(0, 0, W, H);
      dashCtx.save();
      ctxRoundRect(dashCtx, 0, 0, W, H, WIN_R);
      dashCtx.clip();

      // dashboard content below the chrome
      dashCtx.save();
      dashCtx.translate(0, CHROME_H);
      drawDashboard(dashCtx, W, H - CHROME_H, time);
      dashCtx.restore();

      // ---- browser chrome (title bar) ----
      const bar = dashCtx.createLinearGradient(0, 0, 0, CHROME_H);
      bar.addColorStop(0, "#15273f");
      bar.addColorStop(1, "#0e1c30");
      dashCtx.fillStyle = bar;
      dashCtx.fillRect(0, 0, W, CHROME_H);
      dashCtx.fillStyle = "rgba(255,255,255,0.08)"; // hairline under bar
      dashCtx.fillRect(0, CHROME_H - 1, W, 1);

      const midY = CHROME_H / 2;
      // window dots
      const dots = ["#ff5f57", "#febc2e", "#28c840"];
      dots.forEach((col, i) => {
        dashCtx.beginPath();
        dashCtx.fillStyle = col;
        dashCtx.arc(36 + i * 28, midY, 8.5, 0, Math.PI * 2);
        dashCtx.fill();
      });

      // nav glyphs (‹ › ⟳) — subtle, modern
      dashCtx.strokeStyle = "rgba(159,178,200,0.65)";
      dashCtx.lineWidth = 3;
      dashCtx.lineCap = "round";
      dashCtx.lineJoin = "round";
      const navY = midY;
      dashCtx.beginPath(); // back
      dashCtx.moveTo(150, navY - 7); dashCtx.lineTo(143, navY); dashCtx.lineTo(150, navY + 7);
      dashCtx.stroke();
      dashCtx.beginPath(); // forward
      dashCtx.moveTo(178, navY - 7); dashCtx.lineTo(185, navY); dashCtx.lineTo(178, navY + 7);
      dashCtx.stroke();
      dashCtx.beginPath(); // reload arc
      dashCtx.arc(216, navY, 8, Math.PI * 0.35, Math.PI * 1.9);
      dashCtx.stroke();

      // URL pill
      const px = 250;
      const pw = W - px - 70;
      const ph = 44;
      const py = midY - ph / 2;
      ctxRoundRect(dashCtx, px, py, pw, ph, ph / 2);
      dashCtx.fillStyle = "rgba(255,255,255,0.07)";
      dashCtx.fill();
      // lock glyph
      const lx = px + 26;
      dashCtx.strokeStyle = "rgba(170,224,200,0.95)";
      dashCtx.lineWidth = 2.5;
      dashCtx.beginPath();
      dashCtx.arc(lx, midY - 5, 5, Math.PI, 0); // shackle
      dashCtx.stroke();
      dashCtx.fillStyle = "rgba(170,224,200,0.95)";
      ctxRoundRect(dashCtx, lx - 7, midY - 3, 14, 12, 2.5); // body
      dashCtx.fill();
      // url text (on-brand domain)
      dashCtx.fillStyle = "rgba(214,228,242,0.95)";
      dashCtx.font = "600 24px 'Segoe UI', Arial, sans-serif";
      dashCtx.textAlign = "left";
      dashCtx.textBaseline = "middle";
      dashCtx.fillText("erp.noser.com.ph", lx + 22, midY + 1);

      dashCtx.restore();
      dashTex.needsUpdate = true;
    };
    repaintDashboard(0);

    // -------------------------------------------------- frame update
    const update = (time) => {
      if (!reduceMotion && time - lastDash > 0.06) {
        lastDash = time;
        repaintDashboard(time);
      }

      const reveal = smoothstep(0.32, 0.72, progress);

      // camera ease down + parallax. Higher base look so the browser sits lower
      // in the frame at the top (leaves headroom for the headline); same network
      // framing at p=1.
      const camY = 2.0 - progress * 4.6;
      const lookY = 2.5 - progress * 7.4;
      camera.position.x += (parallax.x * 0.6 - camera.position.x) * 0.06;
      camera.position.y += (camY - camera.position.y) * 0.08;
      camera.lookAt(0, lookY, 0);

      // monitor float + cursor parallax
      monitor.rotation.y += (parallax.x * 0.18 - monitor.rotation.y) * 0.06;
      monitor.rotation.x += (-parallax.y * 0.12 - monitor.rotation.x) * 0.06;
      monitor.position.y = 1.7 + (reduceMotion ? 0 : Math.sin(time * 0.8) * 0.08);

      // hover raycast (cards only)
      if (reveal > 0.5 && !reduceMotion) {
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObjects(cardMeshes, false);
        hovered = hits.length ? hits[0].object.userData.gi : -1;
      } else {
        hovered = -1;
      }

      // ---- node positions: base + subtle oscillation + reveal rise ----
      const rise = (1 - reveal) * -1.4; // whole network slides up together on reveal
      const osc = reduceMotion ? 0 : 1;
      const oscTime = time * 1.5; // 5% faster node drift
      let hoverGlow = 0;
      for (let k = 0; k < NET_PTS; k += 1) {
        const n = gnodes[k];
        n.x = n.bx + Math.sin(oscTime * n.fx + n.px) * n.ax * osc;
        n.y = n.by + Math.sin(oscTime * n.fy + n.py) * n.ay * osc + rise;
        n.z = n.bz + Math.sin(oscTime * n.fz + n.pz) * n.az * osc;
        if (n.mesh) {
          const targetHover = hovered === k ? 1 : 0;
          n.hover += (targetHover - n.hover) * 0.12;
          hoverGlow = Math.max(hoverGlow, n.hover);
          n.mesh.position.set(n.x, n.y, n.z);
          n.mesh.material.opacity = reveal;
          n.mesh.scale.setScalar((0.85 + 0.15 * reveal) * (1 + n.hover * 0.1));
        }
      }

      // node dots (non-card nodes)
      const dAttr = dotGeo.getAttribute("position");
      for (let i = 0; i < dotIdx.length; i += 1) {
        const n = gnodes[dotIdx[i]];
        dAttr.setXYZ(i, n.x, n.y, n.z);
      }
      dAttr.needsUpdate = true;
      dotMat.opacity = reveal * 0.95;

      // network lines (rebuilt from live node positions → never disconnect)
      for (let e = 0; e < edges.length; e += 1) {
        const a = gnodes[edges[e][0]];
        const b = gnodes[edges[e][1]];
        lineFlat[e * 6] = a.x; lineFlat[e * 6 + 1] = a.y; lineFlat[e * 6 + 2] = a.z;
        lineFlat[e * 6 + 3] = b.x; lineFlat[e * 6 + 4] = b.y; lineFlat[e * 6 + 5] = b.z;
      }
      lineGeo.setPositions(lineFlat);
      lineMat.opacity = reveal * 0.55;

      // single trunk: hub → computer
      const hub = gnodes[HUB];
      trunkGeo.setPositions([hub.x, hub.y, hub.z, monitorAnchor.x, monitorAnchor.y, monitorAnchor.z]);
      trunkMat.opacity = reveal * (0.6 + hoverGlow * 0.3);

      // hopping pulses: greedy walk toward hub, then up the trunk into the computer
      const getX = (k) => gnodes[k].x;
      const getY = (k) => gnodes[k].y;
      const getZ = (k) => gnodes[k].z;
      const distHub2 = (k) => {
        const ex = gnodes[k].x - hub.x;
        const ey = gnodes[k].y - hub.y;
        const ez = gnodes[k].z - hub.z;
        return ex * ex + ey * ey + ez * ez;
      };
      const randNode = () => 1 + Math.floor(Math.random() * (NET_PTS - 1));
      const pAttr = pulseGeo.getAttribute("position");
      pulseMat.opacity = reveal * 0.95;
      pulseMat.size = 0.18 + hoverGlow * 0.06;
      pulses.forEach((p, idx) => {
        if (p.onTrunk) {
          if (!reduceMotion) p.t += p.speed * 1.8 * 0.016;
          if (p.t >= 1) {
            p.onTrunk = false;
            p.from = randNode();
            p.to = p.from;
            p.t = 1;
          }
          const tt = Math.min(1, p.t);
          pAttr.setXYZ(idx, hub.x + (monitorAnchor.x - hub.x) * tt, hub.y + (monitorAnchor.y - hub.y) * tt, hub.z + (monitorAnchor.z - hub.z) * tt);
          return;
        }
        if (p.t >= 1) {
          p.from = p.to;
          if (p.from === HUB) {
            p.onTrunk = true;
            p.t = 0;
            pAttr.setXYZ(idx, hub.x, hub.y, hub.z);
            return;
          }
          const nbrs = adj[p.from];
          if (!nbrs || nbrs.length === 0) {
            p.from = randNode();
            p.to = p.from;
            p.t = 1;
            pAttr.setXYZ(idx, getX(p.from), getY(p.from), getZ(p.from));
            return;
          }
          // greedy toward hub, with occasional randomness to avoid 2-cycles
          let best = nbrs[0];
          let bestD = distHub2(best);
          for (let n = 1; n < nbrs.length; n += 1) {
            const d = distHub2(nbrs[n]);
            if (d < bestD) {
              bestD = d;
              best = nbrs[n];
            }
          }
          p.to = Math.random() < 0.2 ? nbrs[Math.floor(Math.random() * nbrs.length)] : best;
          p.t = 0;
        }
        if (!reduceMotion) p.t += p.speed * 0.016;
        const tt = Math.min(1, p.t);
        const ax = getX(p.from);
        const ay = getY(p.from);
        const az = getZ(p.from);
        pAttr.setXYZ(idx, ax + (getX(p.to) - ax) * tt, ay + (getY(p.to) - ay) * tt, az + (getZ(p.to) - az) * tt);
      });
      pAttr.needsUpdate = true;

      // ambient particles drift + parallax
      particles.rotation.y = (reduceMotion ? 0 : time * 0.015) + parallax.x * 0.1;
      particles.rotation.x = parallax.y * 0.06;
    };

    // -------------------------------------------------- loop / reduced motion
    let rafId = 0;
    const clock = new THREE.Clock();
    if (reduceMotion) {
      progress = 0;
      update(0);
      renderer.render(scene, camera);
    } else {
      const animate = () => {
        update(clock.getElapsedTime());
        renderer.render(scene, camera);
        rafId = requestAnimationFrame(animate);
      };
      rafId = requestAnimationFrame(animate);
    }

    // -------------------------------------------------- cleanup
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      mount.removeEventListener("pointermove", onPointerMove);
      mount.removeEventListener("pointerleave", onPointerLeave);
      if (resizeObserver) resizeObserver.disconnect();
      else window.removeEventListener("resize", setSize);
      disposables.forEach((d) => d.dispose && d.dispose());
      renderer.dispose();
    };
  }, []);

  return (
    <div ref={mountRef} className={`nz-viz ${className}`} aria-hidden="true">
      <canvas ref={canvasRef} className="nz-viz__canvas" />
    </div>
  );
};

export default PlatformViz;
